// Backup and restore API service
import { API_BASE } from '../config';

/**
 * Export selected sections as backup
 * @param {string[]} sections - Array of section names to export
 * @param {boolean} includeFiles - Whether to include uploaded files
 * @returns {Promise<Object>} Backup data object
 */
export async function exportBackup(sections, includeFiles = false) {
  const response = await fetch(`${API_BASE}/backup/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ sections, includeFiles })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create backup');
  }

  return response.json();
}

/**
 * Preview restore changes before applying
 * @param {Object} backup - Backup data object
 * @returns {Promise<Object>} Preview of changes
 */
export async function previewRestore(backup) {
  const response = await fetch(`${API_BASE}/backup/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ backup })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to preview restore');
  }

  return response.json();
}

/**
 * Restore data from backup
 * @param {Object} backup - Backup data object
 * @param {Object} options - Restore options
 * @returns {Promise<Object>} Restore report
 */
export async function restoreBackup(backup, options = {}) {
  const response = await fetch(`${API_BASE}/backup/restore`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ backup, options })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to restore backup');
  }

  return response.json();
}

/**
 * Download backup as ZIP file
 * @param {Object} backup - Backup data object
 * @param {string} filename - Optional filename
 */
export function downloadBackupZip(backup, filename = null) {
  const defaultName = `fornbo-backup-${new Date().toISOString().split('T')[0]}`;
  const actualName = filename || defaultName;
  
  // Create JSON blob
  const jsonStr = JSON.stringify(backup, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${actualName}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Read backup file from uploaded file
 * @param {File} file - Uploaded file
 * @returns {Promise<Object>} Parsed backup data
 */
export function readBackupFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const backup = JSON.parse(e.target.result);
        resolve(backup);
      } catch (err) {
        reject(new Error('Invalid backup file format'));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read backup file'));
    };
    
    reader.readAsText(file);
  });
}

/**
 * Validate backup file structure
 * @param {Object} backup - Backup data
 * @returns {Object} Validation result
 */
export function validateBackup(backup) {
  const errors = [];
  
  if (!backup) {
    errors.push('Backup data is missing');
    return { valid: false, errors };
  }
  
  if (!backup.version) {
    errors.push('Missing backup version');
  }
  
  if (!Array.isArray(backup.sections)) {
    errors.push('Invalid sections data');
  }
  
  if (!backup.data || typeof backup.data !== 'object') {
    errors.push('Invalid data structure');
  }
  
  if (!backup.timestamp) {
    errors.push('Missing timestamp');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Download restore report
 * @param {Object} report - Restore report
 * @param {string} filename - Optional filename
 */
export function downloadRestoreReport(report, filename = null) {
  const defaultName = `fornbo-restore-report-${new Date().toISOString().split('T')[0]}`;
  const actualName = filename || defaultName;
  
  // Format report as text
  const lines = [
    'FORNBO ÅTERSTÄLLNINGSRAPPORT',
    `=${'='.repeat(50)}`,
    '',
    `Startad: ${new Date(report.startedAt).toLocaleString('sv-SE')}`,
    `Slutförd: ${new Date(report.completedAt).toLocaleString('sv-SE')}`,
    '',
    'ÅTERSTÄLLDA SEKTIONER:',
    `-${'-'.repeat(30)}`
  ];
  
  for (const [section, count] of Object.entries(report.restored)) {
    lines.push(`${section}: ${count} poster`);
  }
  
  if (Object.keys(report.failed).length > 0) {
    lines.push('', 'MISSLYCKADE SEKTIONER:', `-${'-'.repeat(30)}`);
    for (const [section, error] of Object.entries(report.failed)) {
      lines.push(`${section}: ${error}`);
    }
  }
  
  if (report.tempPasswords && report.tempPasswords.length > 0) {
    lines.push('', 'TILLFÄLLIGA LÖSENORD:', `=${'='.repeat(30)}`);
    lines.push('Användare måste byta lösenord vid första inloggning.', '');
    for (const { username, tempPassword } of report.tempPasswords) {
      lines.push(`${username}: ${tempPassword}`);
    }
  }
  
  const text = lines.join('\n');
  const blob = new Blob([text], { type: 'text/plain' });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${actualName}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// Section labels for UI
export const SECTION_LABELS = {
  news: { label: 'Nyheter', icon: '📰', description: 'Nyhetsartiklar och aktuella händelser' },
  documents: { label: 'Dokument', icon: '📄', description: 'Dokument, protokoll och filer' },
  board: { label: 'Styrelse', icon: '👥', description: 'Styrelsemedlemmar och kontaktinformation' },
  funktionarer: { label: 'Funktionärer', icon: '🔧', description: 'Övriga funktionärer och revisorer' },
  contactEntries: { label: 'Kontaktuppgifter', icon: '📧', description: 'E-postadresser och postadress' },
  pages: { label: 'Sidor', icon: '📝', description: 'Alla sidor och innehåll' },
  categories: { label: 'Kategorier', icon: '🏷️', description: 'Dokumentkategorier' },
  users: { label: 'Användare', icon: '👤', description: 'Användarkonton (exklusive lösenord)' },
  matrikel: { label: 'Digital Matrikel', icon: '🏠', description: 'Fastighetsägare och kontaktuppgifter' }
};

// All sections
export const ALL_SECTIONS = Object.keys(SECTION_LABELS);

// Default sections (all)
export const DEFAULT_SECTIONS = [...ALL_SECTIONS];
